<template>
  <el-dialog
    :model-value="visible"
    :title="doc ? doc.name : '预览'"
    width="95%"
    top="2vh"
    destroy-on-close
    @update:model-value="$emit('update:visible', $event)"
  >
    <div v-loading="loading" class="kb-preview">
      <el-alert v-if="loadError" type="error" :closable="false" :title="loadError" />

      <!-- PDF（含 doc/docx 的派生 PDF）：自带的 pdf.js 阅读器，按 Range 只拉看到的页，#page 跳页；
           检索词高亮由 onPdfViewerLoaded 在每页文字层渲染时做。
           只改 fragment 不会让已存在的 iframe 重新导航，所以挂 :key 强制重建。 -->
      <template v-else-if="info.kind === 'pdf'">
        <!-- 阅读器需要较新的浏览器（约 Chrome 94+）；老版 360/QQ 浏览器会白屏，给个兜底 -->
        <div class="kb-preview-fallback">
          显示不出来？<el-link type="primary" @click="$emit('download', doc)">下载原文件</el-link>
        </div>
        <iframe
          ref="pdfFrame"
          :key="pdfSrc"
          :src="pdfSrc"
          class="kb-preview-frame"
          title="文档预览"
        ></iframe>
      </template>

      <template v-else-if="info.kind === 'text'">
        <el-alert v-if="textTip" type="warning" :closable="false" :title="textTip" />
        <template v-else>
          <div class="kb-preview-bar">
            <span class="kb-preview-pos">
              第 {{ (info.start || 0) + 1 }} - {{ windowEnd }} 字 / 共 {{ info.total }} 字
            </span>
            <el-button-group>
              <el-button
                size="small"
                :disabled="info.prev_offset === null || info.prev_offset === undefined"
                @click="jump(info.prev_offset)"
              >
                上一段
              </el-button>
              <el-button
                size="small"
                :disabled="info.next_offset === null || info.next_offset === undefined"
                @click="jump(info.next_offset)"
              >
                下一段
              </el-button>
            </el-button-group>
          </div>
          <!-- before / hit / after 三段都由后端切好，前端不做任何偏移算术。
               hit 是点进来定位的那一处（深色、滚到它）；窗口里其余出现处按关键词浅色标出，
               翻到别的窗口也照样标。pre 里标签之间不能留空白，否则会渲染成多余的空格/换行 -->
          <pre ref="textBox" class="kb-preview-text"
            ><template v-for="(seg, i) in beforeSegs" :key="'b' + i"
              ><mark v-if="seg.hit" class="kb-hit-other">{{ seg.text }}</mark
              ><template v-else>{{ seg.text }}</template></template
            ><mark v-if="info.hit" ref="hitMark" class="kb-hit-main">{{ info.hit }}</mark
            ><template v-for="(seg, i) in afterSegs" :key="'a' + i"
              ><mark v-if="seg.hit" class="kb-hit-other">{{ seg.text }}</mark
              ><template v-else>{{ seg.text }}</template></template
            ></pre
          >
        </template>
      </template>

      <img v-else-if="info.kind === 'image'" :src="rawUrl" class="kb-preview-image" alt="图片预览" />

      <audio
        v-else-if="info.kind === 'audio'"
        :src="rawUrl"
        class="kb-preview-audio"
        controls
      ></audio>

      <el-empty
        v-else-if="info.kind === 'download_only'"
        :description="info.reason || '该格式不支持在线预览，请下载查看'"
      >
        <el-button type="primary" @click="$emit('download', doc)">下载原文件</el-button>
      </el-empty>
    </div>
  </el-dialog>
</template>

<script>
import {
  knowledgeDocPreviewInfo,
  knowledgeDocPreviewUrl,
  pdfPreviewSrc,
} from "@/api/report-system/knowledge-base";
import {
  withTimeout,
  errorText,
  previewQuery,
  previewTextTip,
  previewWindowEnd,
  previewInfoResult,
  splitByKeyword,
  configurePdfViewer,
  attachPdfKeywordHighlight,
} from "@/utils/knowledgeBase";

const PREVIEW_TIMEOUT_MS = 30000;

export default {
  name: "KnowledgeDocPreview",
  props: {
    visible: { type: Boolean, default: false },
    doc: { type: Object, default: null },
    keyword: { type: String, default: "" },
  },
  emits: ["update:visible", "download"],
  data() {
    return {
      loading: false,
      loadError: "",
      info: {},
    };
  },
  computed: {
    pdfSrc() {
      return this.doc ? pdfPreviewSrc(this.doc.id, this.info.page) : "";
    },
    rawUrl() {
      return this.doc ? knowledgeDocPreviewUrl(this.doc.id) : "";
    },
    windowEnd() {
      return previewWindowEnd(this.info);
    },
    beforeSegs() {
      return splitByKeyword(this.info.before, this.keyword);
    },
    afterSegs() {
      return splitByKeyword(this.info.after, this.keyword);
    },
    // 空串表示正常渲染文本窗口；判据在 utils/knowledgeBase.js 里（可单测）
    textTip() {
      return previewTextTip(this.info);
    },
  },
  // pdf.js 阅读器启动时在父页面的 document 上发 webviewerloaded，这是它在读选项之前
  // 留给外层页面的唯一口子（iframe 与本页同源才收得到）
  mounted() {
    document.addEventListener("webviewerloaded", this.onPdfViewerLoaded);
  },
  beforeUnmount() {
    document.removeEventListener("webviewerloaded", this.onPdfViewerLoaded);
  },
  watch: {
    visible(v) {
      if (v) this.load(previewQuery(this.doc, this.keyword));
    },
  },
  methods: {
    onPdfViewerLoaded(e) {
      const win = e.detail && e.detail.source;
      // 只认本组件自己的 iframe：别处的 pdf.js 阅读器、或已关掉的旧 iframe 晚到的事件都不碰
      const frame = this.$refs.pdfFrame;
      if (!win || !frame || win !== frame.contentWindow || !win.PDFViewerApplicationOptions) return;
      configurePdfViewer(win.PDFViewerApplicationOptions);
      // 关键词与目标页取打开那一刻的值（弹窗 destroy-on-close，下次打开是新的 iframe）
      const keyword = this.keyword;
      // 页码未知（0）时不做首次滚动：pageNumber 从 1 起，永远对不上
      const targetPage = this.info.page || 0;
      const app = win.PDFViewerApplication;
      if (!keyword || !app) return;
      // pdf.js 默认的高亮是很淡的粉紫色，换成与文本预览一致的黄色（iframe 同源，可直接往里加样式）
      const style = win.document.createElement("style");
      style.textContent = ".textLayer .highlight.appended { --highlight-bg-color: rgb(255 197 61 / 0.6); }";
      win.document.head.appendChild(style);
      app.initializedPromise.then(() => attachPdfKeywordHighlight(app, keyword, targetPage));
    },
    // 翻窗时不带 keyword：后端只在点进来的那一处切 hit，翻窗后没有主命中；
    // 窗口里其余出现处仍由前端 splitByKeyword 按 props.keyword 浅色标出
    jump(offset) {
      this.load({ offset });
    },
    async load(params) {
      if (!this.doc) return;
      this.loading = true;
      this.loadError = "";
      // 必须先清掉：destroy-on-close 只销毁弹窗内容、不重置组件 data，
      // 不清的话「看完一份 PDF 再点一份 txt」会先按旧的 kind='pdf' 渲染一次 ——
      // iframe 的 :key 变了，于是真的去请求新文档的 /preview，而 txt 不在内联白名单，
      // 服务端回 400，用户看到一次闪烁
      this.info = {};
      try {
        const res = await withTimeout(
          knowledgeDocPreviewInfo(this.doc.id, params),
          PREVIEW_TIMEOUT_MS
        );
        // res 就是信封（request.js 的响应拦截器已经剥过 axios 那层），别再取一次 .data
        const { ok, info, error } = previewInfoResult(res);
        if (!ok) {
          this.info = {};
          this.loadError = error;
          return;
        }
        this.info = info;
        await this.$nextTick();
        // 滚到命中处；浏览器不支持 scrollIntoView 时退化为不滚动，不影响内容可见
        const mark = this.$refs.hitMark;
        if (mark && mark.scrollIntoView) mark.scrollIntoView({ block: "center" });
      } catch (e) {
        this.info = {};
        this.loadError = errorText(e) || "预览失败";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.kb-preview {
  min-height: 300px;
}
/* 弹窗 top 2vh；减去标题栏、内边距与兜底提示行约 130px，正文区几乎占满一屏 */
.kb-preview-fallback {
  margin-bottom: 4px;
  text-align: right;
  color: #909399;
  font-size: 12px;
}
.kb-preview-frame {
  width: 100%;
  height: calc(96vh - 130px);
  border: 1px solid #dcdfe6;
}
.kb-preview-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.kb-preview-pos {
  color: #909399;
  font-size: 13px;
}
.kb-preview-text {
  max-height: calc(96vh - 150px);
  overflow: auto;
  margin: 0;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #dcdfe6;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.8;
}
.kb-preview-text mark {
  padding: 0;
  color: inherit;
}
.kb-preview-text .kb-hit-other {
  background: #fff1b8;
}
.kb-preview-text .kb-hit-main {
  background: #ffc53d;
  outline: 2px solid #fa8c16;
}
.kb-preview-image {
  max-width: 100%;
  max-height: calc(96vh - 110px);
  display: block;
  margin: 0 auto;
}
.kb-preview-audio {
  width: 100%;
  margin-top: 40px;
}
</style>
